import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

export const Packages = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const p = t.packages;
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {});
  }, []);

  const field = (pkg, key) => (lang === "en" ? pkg[`${key}_en`] || pkg[key] : pkg[key]);

  return (
    <section id="packages" className="py-20 md:py-32 bg-sand-surface" data-testid="packages-section">
      <div className="px-6 md:px-12 lg:px-20 mx-auto max-w-7xl">
        <Reveal className="max-w-2xl mb-16">
          <p className="font-body text-xs tracking-[0.25em] uppercase text-teal font-bold mb-4">{p.overline}</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-jungle mb-5">{p.title}</h2>
          <p className="font-body text-jungle-light/80 leading-relaxed">{p.subtitle}</p>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg, i) => (
            <Reveal key={pkg.id} delay={i * 0.06}>
              <div
                data-testid={`package-card-${pkg.slug}`}
                className={`group relative bg-white border flex flex-col h-full overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                  pkg.featured ? "border-teal ring-1 ring-teal" : "border-border"
                }`}
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={pkg.image} alt={field(pkg, "name")} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-jungle/70 to-transparent" />
                  {pkg.featured && (
                    <span className="absolute top-4 left-4 bg-teal text-sand font-body text-[10px] uppercase tracking-wider px-3 py-1" data-testid={`package-featured-${pkg.slug}`}>
                      {p.featured}
                    </span>
                  )}
                  <div className="absolute bottom-4 left-5">
                    <span className="font-body text-[10px] uppercase tracking-[0.2em] text-teal font-bold block">{pkg.duration ? field(pkg, "duration") : ""}</span>
                    <h3 className="font-heading text-2xl font-semibold text-sand leading-tight">{field(pkg, "name")}</h3>
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <p className="font-body text-sm text-jungle-light/80 mb-5">{field(pkg, "subtitle")}</p>
                  <p className="font-body text-[11px] uppercase tracking-wider text-jungle-light/50 mb-2">{p.includes}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {field(pkg, "includes").map((inc, k) => (
                      <li key={k} className="flex items-start gap-2 font-body text-sm text-jungle">
                        <Check size={15} className="text-teal mt-0.5 shrink-0" /> {inc}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-end justify-between border-t border-border pt-5">
                    <div>
                      <span className="font-heading text-4xl font-semibold text-jungle">${pkg.price.toLocaleString()}</span>
                      <span className="font-body text-xs text-jungle-light/60 ml-1">USD</span>
                    </div>
                    <button
                      onClick={() => navigate(`/checkout/${pkg.id}`)}
                      data-testid={`package-buy-${pkg.slug}`}
                      className="group/btn inline-flex items-center gap-2 bg-teal text-sand hover:bg-teal-dark font-body text-sm px-5 py-3 transition-colors"
                    >
                      {p.buy}
                      <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
